"use client"

import NextLink from 'next/link'
import throttle from 'lodash/fp/throttle'
import { useEffect, useState } from 'react'
import slugify from 'slugify'
import useAnalytics from '@hooks/use-analytics'
import { Icon, NavBar, NavItem } from '@shc/ui'
import { getIconFromContentful } from '@lib/icons'
import { routeMapper } from '@lib/menu-item-mapper'
import { navBarAlignMap } from '@lib/token-maps'
import type { TypeNavBarBlockFields } from '@lib/generated-types'
import { usePathname } from 'next/navigation'

/**
 * NavBarBlock is a React functional component that renders a navigation bar
 * with menu items and handles active section highlighting based on scroll position.
 *
 * @param {TypeNavBarBlockFields} props - The props for the NavBarBlock component.
 * @param {TypeNavBarBlockFields['sectionLink']} props.sectionLink - The section link used to generate the ID for the navigation bar.
 * @param {TypeNavBarBlockFields['menuItems']} props.menuItems - An array of menu items to be displayed in the navigation bar.
 * Each menu item contains fields such as `link`, `anchorOrExternalLink`, `icon`, and `title`.
 * @param {TypeNavBarBlockFields['alignment']} props.alignment - The alignment of the navigation bar, either 'left' or 'center'.
 *
 * @returns {JSX.Element} The rendered navigation bar component.
 *
 * @remarks
 * - The component uses `useEffect` to set the active section based on the current URL hash
 *   and to handle scroll events for updating the active section dynamically.
 * - The `throttle` function is used to optimize scroll event handling.
 * - The `routeMapper` function is used to map menu item links to routes.
 * - The `track` function is used to track navigation click events for analytics.
 *
 * @example
 * ```tsx
 * <NavBarBlock
 *   sectionLink="example-section"
 *   alignment="center"
 *   menuItems={[
 *     { fields: { link: '/home', title: 'Home', icon: 'home-icon' } },
 *     { fields: { link: '/about', title: 'About', icon: 'about-icon' } },
 *   ]}
 * />
 * ```
 */
const NavBarBlock = ({ sectionLink, menuItems, alignment }: TypeNavBarBlockFields) => {
  const pn = usePathname()
  const throttleMs = 100
  const subNavBarHeight = 64
  const { track } = useAnalytics()
  const [activeSection, setActiveSection] = useState<string | null>(null)

  const sectionElementIds = menuItems.map(({ fields }) => {
    const { route } = routeMapper(fields.link, fields.anchorOrExternalLink)
    const sectionIndex = route.indexOf('#')
    if (sectionIndex != -1) return route.substring(sectionIndex + 1)
    return null
  })

  useEffect(() => {
    setActiveSection(typeof window !== 'undefined' ? window.location.hash.slice(1) : null)
    const targetElem = document.getElementById(window.location.hash.slice(1))
    targetElem?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const handleScroll = throttle(throttleMs, () => {
      for (const sectionElementId of sectionElementIds) {
        if (sectionElementId) {
          const section = document.getElementById(sectionElementId)
          if (
            section &&
            section.getBoundingClientRect().top < window.screenY + subNavBarHeight &&
            section.getBoundingClientRect().bottom > window.screenY + subNavBarHeight
          ) {
            setActiveSection(sectionElementId)
          }
        }
      }
    })

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <NavBar
      id={sectionLink && slugify(sectionLink)}
      alignment={navBarAlignMap[alignment]}
      sticky
      data-block="navBar"
    >
      {menuItems.map(({ fields }) => {
        const { route, isInternal } = routeMapper(fields.link, fields.anchorOrExternalLink)
        const icon = getIconFromContentful(fields.icon)
        const active = route.indexOf('#') === 0 ? activeSection === route.split('#')[1] : !activeSection && route === pn
        const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          track({
            event: {
              name: 'navigation_click',
              data: {
                navigation_tree: fields.title,
                navigation_level: 1,
                navigation_subject: fields.title,
                navigation_url: e.currentTarget.href,
              },
            },
            contexts: [{ name: 'section', data: { section_name: 'subnav' } }],
          })
          if( route.indexOf('#') === 0 ){
            setActiveSection(route.indexOf('#') === 0 ? route.split('#')[1] : null)
            const targetElem = document.getElementById(activeSection)
            targetElem?.scrollIntoView({ behavior: 'smooth' })
            window.history.replaceState(null, '', String(e.currentTarget))
          }
        }
        return (
          <NavItem
            as={NextLink}
            active={active}
            key={fields.title}
            href={route}
            target={isInternal ? undefined : '_blank'}
            rel={isInternal ? undefined : 'noopener noreferrer'}
            onClick={onClick}>
            {fields.icon && <Icon icon={icon} className="pr-1.5" />}
            {fields.title}
          </NavItem>
        )
      })}
    </NavBar>
  )
}

export default NavBarBlock

